import { leadPushBlockers } from '@wr/shared';
import { ApiError, get } from '../../api.js';
import type { LocalRecord, LocalStore } from '../../offline/store.js';

/**
 * People's data (brief section 6.5): from the portal when there is network;
 * without it, what this device keeps (the person's own records and the
 * current event's), filtered the same way.
 */
export type EventFilter = string; // an event ID, 'none', 'past' or 'all'
export type StatusFilter = 'all' | 'todo' | 'synced' | 'mine';

export interface PeopleQuery {
  q: string;
  event: EventFilter;
  status: StatusFilter;
  page: number;
  /** Created from this day, through that one (the device's days, YYYY-MM-DD; Nov 3, 2026). */
  from?: string;
  to?: string;
}

/** The instants of a day range on this device: from its first moment, before the next day. */
export function dayRange(from?: string, to?: string): { start?: string; end?: string } {
  const at = (day: string) => new Date(`${day}T00:00:00`);
  const ok = (day?: string) =>
    !!day && /^\d{4}-\d{2}-\d{2}$/.test(day) && !Number.isNaN(at(day).getTime());
  const out: { start?: string; end?: string } = {};
  if (ok(from)) out.start = at(from!).toISOString();
  if (ok(to)) {
    const end = at(to!);
    end.setDate(end.getDate() + 1);
    out.end = end.toISOString();
  }
  return out;
}

const inRange = (created: unknown, range: { start?: string; end?: string }) => {
  if (!range.start && !range.end) return true;
  if (typeof created !== 'string') return false;
  const t = Date.parse(created);
  return (
    (!range.start || t >= Date.parse(range.start)) && (!range.end || t < Date.parse(range.end))
  );
};

export type ContactRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  job_title: string | null;
  company: string | null;
  email: string | null;
  source: string;
  reading_status: string | null;
  to_check: number;
  hubspot_status: string;
  created_at: string | null;
  created_by: string;
  event_id: string | null;
  lead_count: number;
};

export type LeadRow = {
  id: string;
  company_name: string | null;
  deal_name: string | null;
  qualification: string | null;
  stage_id: string | null;
  hubspot_status: string;
  created_at: string | null;
  created_by: string;
  owner_user_id: string | null;
  event_id: string | null;
  event_code: string | null;
  contacts: string[];
  /** Something still blocks its push (brief section 2, rule 9). */
  to_document: boolean;
};

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  /** True when the list comes from this device (no network). */
  offline?: boolean;
}

const PAGE_SIZE = 20;

function search(query: PeopleQuery): string {
  const p = new URLSearchParams();
  if (query.q.trim()) p.set('q', query.q.trim());
  p.set('event', query.event);
  p.set('status', query.status);
  p.set('page', String(query.page));
  const range = dayRange(query.from, query.to);
  if (range.start) p.set('createdFrom', range.start);
  if (range.end) p.set('createdBefore', range.end);
  return p.toString();
}

/** No network, or the portal not answering: the device's copy. */
export function isUnreachable(error: unknown): boolean {
  return error instanceof ApiError && (error.code === 'offline' || error.status >= 500);
}

/** Any record with these fields (a pulled record or a list row). */
type Fields = Record<string, unknown>;

export const fullName = (c: Fields) =>
  [c.first_name, c.last_name].filter((x) => typeof x === 'string' && x.trim()).join(' ');

function matches(words: string[], text: string) {
  const t = text.toLowerCase();
  return words.every((w) => t.includes(w));
}

function eventMatches(row: Fields, event: EventFilter, events: LocalRecord[]) {
  if (event === 'all') return true;
  if (event === 'none') return !row.event_id;
  if (event === 'past') {
    return events.some((e) => e.id === row.event_id && e.status === 'completed');
  }
  return row.event_id === event;
}

function pageOf<T>(items: T[], page: number): Page<T> {
  return {
    items: items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    total: items.length,
    page,
    pageSize: PAGE_SIZE,
    offline: true,
  };
}

const newestFirst = (a: Fields, b: Fields) =>
  String(b.created_at ?? '9999').localeCompare(String(a.created_at ?? '9999'));

export function contactTodo(c: Fields): boolean {
  const toCheck = Array.isArray(c.needs_verification)
    ? c.needs_verification.length
    : Number(c.to_check ?? 0);
  return (
    !c.first_name ||
    !c.last_name ||
    !c.email ||
    !c.company ||
    toCheck > 0 ||
    ['waiting', 'reading', 'failed'].includes(String(c.reading_status ?? ''))
  );
}

export async function listContacts(
  query: PeopleQuery,
  store: LocalStore | null,
  userId: string,
): Promise<Page<ContactRow>> {
  try {
    return await get<Page<ContactRow>>(`/people/contacts?${search(query)}`);
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
  }
  const [contacts, links, events] = await Promise.all([
    store.listRecords('contact'),
    store.listRecords('lead_contact'),
    store.listRecords('event'),
  ]);
  const words = query.q.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = contacts
    .filter((c) => !c.deleted)
    .filter((c) => matches(words, `${fullName(c)} ${c.company ?? ''} ${c.email ?? ''}`))
    .filter((c) => eventMatches(c, query.event, events))
    .filter((c) => inRange(c.created_at, dayRange(query.from, query.to)))
    .filter((c) =>
      query.status === 'mine'
        ? !c.created_by || c.created_by === userId
        : query.status === 'synced'
          ? c.hubspot_status === 'synced'
          : query.status === 'todo'
            ? contactTodo(c)
            : true,
    )
    .sort(newestFirst)
    .map((c): ContactRow => ({
      id: c.id,
      first_name: (c.first_name as string) ?? null,
      last_name: (c.last_name as string) ?? null,
      job_title: (c.job_title as string) ?? null,
      company: (c.company as string) ?? null,
      email: (c.email as string) ?? null,
      source: (c.source as string) ?? 'manual',
      reading_status: (c.reading_status as string) ?? null,
      to_check: Array.isArray(c.needs_verification) ? c.needs_verification.length : 0,
      hubspot_status: (c.hubspot_status as string) ?? 'pending',
      created_at: (c.created_at as string) ?? null,
      created_by: (c.created_by as string) ?? userId,
      event_id: (c.event_id as string) ?? null,
      lead_count: links.filter((l) => l.contact_id === c.id).length,
    }));
  return pageOf(rows, query.page);
}

export async function listLeads(
  query: PeopleQuery,
  store: LocalStore | null,
  userId: string,
): Promise<Page<LeadRow>> {
  try {
    return await get<Page<LeadRow>>(`/people/leads?${search(query)}`);
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
  }
  const [leads, links, contacts, events] = await Promise.all([
    store.listRecords('lead'),
    store.listRecords('lead_contact'),
    store.listRecords('contact'),
    store.listRecords('event'),
  ]);
  const words = query.q.toLowerCase().split(/\s+/).filter(Boolean);
  const rows = leads
    .filter((l) => !l.deleted)
    .map((l): LeadRow => {
      const names = links
        .filter((x) => x.lead_id === l.id)
        .sort((a, b) => Number(b.is_primary) - Number(a.is_primary))
        .map((x) => contacts.find((c) => c.id === x.contact_id))
        .filter((c): c is LocalRecord => Boolean(c))
        .map((c) => fullName(c) || String(c.email ?? ''));
      return {
        id: l.id,
        company_name: (l.company_name as string) ?? null,
        deal_name: (l.deal_name as string) ?? null,
        qualification: (l.qualification as string) ?? null,
        stage_id: (l.stage_id as string) ?? null,
        hubspot_status: (l.hubspot_status as string) ?? 'pending',
        created_at: (l.created_at as string) ?? null,
        created_by: (l.created_by as string) ?? userId,
        owner_user_id: (l.owner_user_id as string) ?? null,
        event_id: (l.event_id as string) ?? null,
        event_code: (events.find((e) => e.id === l.event_id)?.event_code as string) ?? null,
        contacts: names,
        to_document: leadPushBlockers(l).length > 0,
      };
    })
    .filter((l) =>
      matches(words, `${l.company_name ?? ''} ${l.deal_name ?? ''} ${l.contacts.join(' ')}`),
    )
    .filter((l) => eventMatches(l, query.event, events))
    .filter((l) => inRange(l.created_at, dayRange(query.from, query.to)))
    .filter((l) =>
      query.status === 'mine'
        ? l.created_by === userId || l.owner_user_id === userId
        : query.status === 'synced'
          ? l.hubspot_status === 'synced'
          : query.status === 'todo'
            ? l.to_document
            : true,
    )
    .sort(newestFirst);
  return pageOf(rows, query.page);
}

/** A lead as the contact sheet lists it. */
export interface SheetLead {
  id: string;
  company_name: string | null;
  deal_name: string | null;
  qualification: string | null;
  interest: string | null;
  role: string | null;
  pain: string | null;
  hubspot_status: string;
  created_at: string | null;
  is_primary: boolean;
  /** For a similar open lead (Nov 3, 2026). */
  applications?: string[] | null;
  event_id?: string | null;
}

export interface ContactSheetData {
  contact: LocalRecord;
  leads: SheetLead[];
  createdByName: string | null;
  eventCode: string | null;
  /** The contact's page in HubSpot, once sent. */
  hubspotUrl?: string | null;
  /** True when it comes from this device (no network). */
  offline?: boolean;
}

export async function loadContact(
  id: string,
  store: LocalStore | null,
  me: { id: string; name: string },
): Promise<ContactSheetData | null> {
  try {
    const data = await get<ContactSheetData>(`/contacts/${encodeURIComponent(id)}`);
    // What this device changed and has not sent yet shows too.
    const kept = store ? await store.getRecord('contact', id) : null;
    const waiting = store
      ? (await store.outbox()).filter(
          (i) => i.op.entity === 'contact' && i.op.action === 'upsert' && i.op.id === id,
        )
      : [];
    if (kept && waiting.length > 0) {
      for (const item of waiting) {
        if (item.op.action === 'upsert') Object.assign(data.contact, item.op.fields);
      }
    }
    return data;
  } catch (error) {
    if (!store || !isUnreachable(error)) throw error;
  }
  const contact = await store.getRecord('contact', id);
  if (!contact || contact.deleted) return null;
  const [links, leads, events] = await Promise.all([
    store.listRecords('lead_contact'),
    store.listRecords('lead'),
    store.listRecords('event'),
  ]);
  return {
    contact,
    leads: links
      .filter((l) => l.contact_id === id)
      .map((l) => {
        const lead = leads.find((x) => x.id === l.lead_id);
        return lead
          ? ({ ...(lead as object), is_primary: Boolean(l.is_primary) } as unknown as SheetLead)
          : null;
      })
      .filter((l): l is SheetLead => l !== null),
    createdByName: !contact.created_by || contact.created_by === me.id ? me.name : null,
    eventCode: (events.find((e) => e.id === contact.event_id)?.event_code as string) ?? null,
    offline: true,
  };
}

/** The colour of a person's initials, from their name (as WR Scanner and the prototype). */
const COLORS = ['#CC1111', '#4466CC', '#8855CC', '#AA5588', '#2277AA', '#AA7711', '#227755'];
export function avatarColor(name: string): string {
  const sum = [...name].reduce((a, c) => a + c.charCodeAt(0), 0);
  return COLORS[sum % COLORS.length]!;
}

export function initialsOf(c: Fields) {
  const text = fullName(c) || String(c.company ?? '');
  return (
    text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]!.toUpperCase())
      .join('') || '?'
  );
}

/** "8:01 p.m." today, "Oct 27, 2026" before (the prototype's list). */
export function whenShort(value: string | null): string {
  if (!value) return 'Not sent yet';
  const date = new Date(value);
  if (date.toDateString() === new Date().toDateString()) {
    return date.toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
